type Rgba = readonly [number, number, number, number];

export type ChartPalette = Record<'cpu' | 'memory' | 'process' | 'text' | 'muted' | 'border' | 'surface', Rgba>;

export function chartColor(color: Rgba, alpha = color[3] / 255): string {
    return `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${alpha})`;
}

export function readChartPalette(probe: HTMLElement): ChartPalette {
    const document = probe.ownerDocument;
    const dark = document.documentElement.classList.contains('app-dark');
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('A 2D canvas is required to render the system monitor');

    // Resolve CSS variables and modern colours in the browser before passing them to
    // Chart.js, whose colour parser does not understand var(), oklch() or color-mix().
    const read = (color: string): Rgba => {
        probe.style.color = color;
        context.clearRect(0, 0, 1, 1);
        context.fillStyle = getComputedStyle(probe).color;
        context.fillRect(0, 0, 1, 1);
        const pixel = context.getImageData(0, 0, 1, 1).data;
        return [pixel[0], pixel[1], pixel[2], pixel[3]];
    };

    return {
        cpu: read(`var(--p-primary-${dark ? '400' : '700'}, var(--primary-color, ${dark ? '#4ade80' : '#15803d'}))`),
        memory: read(`var(--p-orange-${dark ? '400' : '600'}, ${dark ? '#fb923c' : '#ea580c'})`),
        process: read(`var(--p-blue-${dark ? '400' : '600'}, ${dark ? '#60a5fa' : '#2563eb'})`),
        text: read(`var(--text-color, ${dark ? '#f3f4f6' : '#1f2937'})`),
        muted: read(`var(--text-color-secondary, ${dark ? '#cbd5e1' : '#4b5563'})`),
        border: read(`var(--surface-border, ${dark ? '#4b5563' : '#d1d5db'})`),
        surface: read(`var(--surface-overlay, var(--surface-card, ${dark ? '#1f2937' : '#ffffff'}))`)
    };
}
